<template>
  <a-layout-footer class="footer">
    <div class="container footer-inner">
      <a-row :gutter="[32, 40]" class="footer-main">
        <!-- Brand + contact -->
        <a-col :xs="24" :sm="12" :md="6" :lg="6">
          <div class="footer-column footer-brand">
            <div class="footer-brand__head">
              <div class="footer-brand__name">
                {{ brandName }}
              </div>
              <div class="footer-brand__divider" aria-hidden="true"></div>
              <p class="footer-brand__tagline">
                {{ brandTagline }}
              </p>
            </div>

            <div class="footer-contact">
              <p v-if="companys.address">
                <EnvironmentOutlined />
                <span>{{ companys.address }}</span>
              </p>
              <p v-if="companys.phone">
                <PhoneOutlined />
                <a :href="`tel:${companys.phone}`">{{ companys.phone }}</a>
              </p>
              <p v-if="companys.email">
                <MailOutlined />
                <a :href="`mailto:${companys.email}`">{{ companys.email }}</a>
              </p>
            </div>

            <div class="footer-social">
              <a
                class="social-icon"
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="$t('text.facebook')"
              >
                <FacebookOutlined />
              </a>
              <a
                class="social-icon"
                href="https://www.google.com"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="$t('text.google')"
              >
                <GoogleOutlined />
              </a>
              <a
                class="social-icon"
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <InstagramOutlined />
              </a>
            </div>
          </div>
        </a-col>

        <!-- Dynamic menu columns from API -->
        <a-col
          v-for="(menu, index) in menuFooter"
          :key="index"
          :xs="24"
          :sm="12"
          :md="6"
          :lg="6"
        >
          <div class="footer-column">
            <h4 class="footer-title">{{ translateLabel(menu.linkLabel) }}</h4>
            <ul class="footer-links">
              <li v-for="(child, cindex) in menu.children" :key="cindex">
                <router-link :to="child.linkTo || '/'">
                  {{ translateLabel(child.linkLabel) }}
                </router-link>
              </li>
            </ul>
          </div>
        </a-col>
      </a-row>

      <div class="footer-bottom">
        <p>{{ $t("text.copyright") }}</p>
      </div>
    </div>
  </a-layout-footer>
</template>

<script lang="ts" setup>
import { watch, ref, onMounted, computed } from "vue";
import store from "@/store";
import commons from "@/utils/Common";
import { apiRedis } from "@/api/index";
import {
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  FacebookOutlined,
  InstagramOutlined,
  GoogleOutlined,
} from "@ant-design/icons-vue";
import { useI18n } from "vue-i18n";

const { t, te } = useI18n();

const FOOTER_LABEL_KEYS: Record<string, string> = {
  "tài khoản": "text.account",
  "tai khoan": "text.account",
  "chăm sóc khách hàng": "text.customerSupport",
  "cham soc khach hang": "text.customerSupport",
  "đăng nhập": "text.login",
  "dang nhap": "text.login",
  "đăng ký": "text.register",
  "dang ky": "text.register",
  "giỏ hàng": "text.cart",
  "gio hang": "text.cart",
  "kiểm tra đơn hàng": "text.trackOrder",
  "kiem tra don hang": "text.trackOrder",
  "hướng dẫn mua hàng": "text.howToOrder",
  "huong dan mua hang": "text.howToOrder",
  "hướng dẫn thanh toán": "text.paymentGuide",
  "huong dan thanh toan": "text.paymentGuide",
  "phương thức thanh toán": "text.paymentMethods",
  "phuong thuc thanh toan": "text.paymentMethods",
  "chính sách đổi trả": "text.returnExchangePolicy",
  "chinh sach doi tra": "text.returnExchangePolicy",
  "hướng dẫn đổi trả": "text.returnGuide",
  "huong dan doi tra": "text.returnGuide",
  "hướng dẫn bảo hành": "text.warrantyGuide",
  "huong dan bao hanh": "text.warrantyGuide",
  "chính sách bảo hành": "text.warrantyPolicy",
  "chinh sach bao hanh": "text.warrantyPolicy",
  "chính sách giao hàng": "text.deliveryPolicy",
  "chinh sach giao hang": "text.deliveryPolicy",
  "câu hỏi thường gặp": "text.faq",
  "cau hoi thuong gap": "text.faq",
  "giới thiệu": "text.aboutUs",
  "gioi thieu": "text.aboutUs",
  "về kima home": "text.aboutKimaHome",
  "ve kima home": "text.aboutKimaHome",
  "hệ thống cửa hàng": "text.storeLocations",
  "he thong cua hang": "text.storeLocations",
  "khuyến mại": "text.promotions",
  "khuyen mai": "text.promotions",
  "tuyển dụng": "text.careers",
  "tuyen dung": "text.careers",
  "liên hệ": "text.contact",
  "lien he": "text.contact",
  "cam kết sản phẩm chính hãng": "text.authenticCommitment",
  "cam ket san pham chinh hang": "text.authenticCommitment",
  "chính sách bảo mật": "text.privacyPolicyTitle",
  "chinh sach bao mat": "text.privacyPolicyTitle",
  "sản phẩm": "text.products",
  "san pham": "text.products",
  "bộ sưu tập": "text.collections",
  "bo suu tap": "text.collections",
  "trang chủ": "text.home",
  "trang chu": "text.home",
  "đơn hàng": "text.allOrders",
  "don hang": "text.allOrders",
};

const normalizeLabel = (label: string) => label.trim().toLowerCase();

const translateLabel = (label?: string) => {
  if (!label) return "";
  if (label.includes(".")) {
    return te(label) ? t(label) : label;
  }
  const key = FOOTER_LABEL_KEYS[normalizeLabel(label)];
  return key ? t(key) : label;
};

const companys = ref({
  summary: "",
  sub_name: "",
  address: "",
  phone: "",
  name: "",
  description: "",
  logo: "",
  id: null as number | null,
  email: "",
});

const brandName = computed(() => {
  const raw =
    companys.value.sub_name || companys.value.name || "KIMAHOME";
  return String(raw).toUpperCase();
});

const brandTagline = computed(() => {
  const s = (companys.value.summary || "").trim();
  if (s && s.length <= 40) return s.toUpperCase();
  return t("text.luxuryFurniture").toUpperCase();
});

const menuFooter = ref<any[]>([]);

onMounted(() => {
  loadMenuFooter();
  cokiesCompany();
});

const cokiesCompany = () => {
  const data = commons.getCokiesCompany();
  if (data) companys.value = data;
};

const isLCompanyInfor = computed(() => store.getters.isLCompanyInfor);
watch(isLCompanyInfor, () => {
  cokiesCompany();
});

const loadMenuFooter = async () => {
  await apiRedis("default-fotter").then((res) => {
    const response: any = res;
    let data = response.data.data;
    if (typeof data === "string") {
      data = JSON.parse(data);
    }
    menuFooter.value = data || [];
  });
};
</script>

<style scoped>
.footer.ant-layout-footer {
  background: var(--primary-dark, #102542);
  color: rgba(255, 255, 255, 0.85);
  /* ~1.8× logo height — match mock top breathing room */
  padding: 80px 0 32px;
  margin-top: 0;
  text-align: left;
}

.footer-inner {
  max-width: 1200px;
}

.footer-column {
  text-align: left;
}

/* ===== Brand ===== */
.footer-brand__head {
  text-align: center;
  margin-bottom: 28px;
}

.footer-brand__name {
  margin: 0;
  font-family: "Playfair Display", Georgia, serif;
  font-size: 28px;
  font-weight: 500;
  letter-spacing: 0.2em;
  color: var(--default-header-color, #b19663);
  line-height: 1.15;
  text-transform: uppercase;
}

.footer-brand__divider {
  width: 72px;
  height: 1px;
  margin: 12px auto 10px;
  background: var(--default-header-color, #b19663);
}

.footer-brand__tagline {
  margin: 0;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--default-header-color, #b19663);
}

.footer-contact {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}

.footer-contact p {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.82);
}

.footer-contact a {
  color: rgba(255, 255, 255, 0.82);
  text-decoration: none;
}

.footer-contact a:hover {
  color: var(--default-header-color, #b19663);
}

.footer-contact :deep(.anticon) {
  margin-top: 3px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  flex-shrink: 0;
}

.footer-social {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 22px;
}

.social-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.75);
  font-size: 18px;
  transition: color 0.2s ease;
}

.social-icon:hover {
  color: var(--default-header-color, #b19663);
}

/* ===== Menu columns ===== */
.footer-title {
  display: inline-block;
  margin: 0 0 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #fff;
  line-height: 1.35;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin: 0 0 11px;
  line-height: 1.4;
}

.footer-links a {
  color: #fff;
  text-decoration: none;
  font-size: 13px;
  transition: color 0.2s ease;
}

.footer-links a:hover {
  color: var(--default-header-color, #b19663);
}

/* ===== Bottom ===== */
.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  margin-top: 48px;
  padding-top: 22px;
  text-align: center;
}

.footer-bottom p {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
}

@media (max-width: 767px) {
  .footer.ant-layout-footer {
    padding: 56px 0 24px;
    margin-top: 40px;
  }

  .footer-brand__name {
    font-size: 24px;
    letter-spacing: 0.16em;
  }

  .footer-title {
    margin-bottom: 16px;
  }
}
</style>
